import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1770" data-mp="2heb8ysixi3s7">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1771" data-mp="1kplmeowcwmzw">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1772" data-mp="1oq5irq93vmkz">
          <div className="flex items-center h-16" data-id="element-1773" data-mp="1os89kci31jqr">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1774" data-mp="1togsm0zehx37">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1775" data-mp="1ypwe1u5hq5m4">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1776" data-mp="353cbjz52ykpk">
                <Home size={18} data-id="element-1777" data-mp="hvy44lgaxw5k" />
                <span className="hidden sm:inline" data-id="element-1778" data-mp="hwzqhm2rq52l">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1779" data-mp="vvzecrigifxp">
                <BarChart3 size={18} data-id="element-1780" data-mp="15s2ejrtr04i0" />
                <span className="hidden sm:inline" data-id="element-1781" data-mp="10v283fszkzqd">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1782" data-mp="10szhatk0f2kl">
                <FileText size={18} data-id="element-1783" data-mp="1ojlic0vljml3" />
                <span className="hidden sm:inline" data-id="element-1784" data-mp="1oko4jvhz50uo">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1785" data-mp="1jmv1hbbywmym">
                <UserCog size={18} data-id="element-1786" data-mp="1t8xo8qinfr6v" />
                <span className="hidden sm:inline" data-id="element-1787" data-mp="1t9pavbu9qel0">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1788" data-mp="1ya3gkn8edcfv">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1789" data-mp="1kfu3ry9l6e2q">
                <Bell size={20} data-id="element-1790" data-mp="1aiobwnt5ej1g" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1791" data-mp="1ajr0gp3009e7"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1792" data-mp="1fhsjxb4c6aq7">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1793" data-mp="11hhqq1fk0k1v">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1794" data-mp="386s4cnig8quz">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1795" data-mp="2yffajqd3bqmb">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1796" data-mp="2tf15mh6scwsi" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1797" data-mp="3m59oycd25n8o">
                    <div className="py-2" data-id="element-1798" data-mp="3h5dje8oppnr0">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1799" data-mp="3h8jb7voq73qf">
                        <Users size={18} className="text-gray-600" data-id="element-1800" data-mp="21pesea7888dz" />
                        <span className="font-medium text-gray-900" data-id="element-1801" data-mp="21sapxxf5wv1w">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1802" data-mp="laaeblx3y3h6">
                        <Home size={18} className="text-gray-600" data-id="element-1803" data-mp="q6qy63x9xvog" />
                        <span className="font-medium text-gray-900" data-id="element-1804" data-mp="v0v6orm6ubrn">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1805" data-mp="14woux899rtp9">
                        <UserCog size={18} className="text-gray-600" data-id="element-1806" data-mp="18zuvbz9paqnt" />
                        <span className="font-medium text-gray-900" data-id="element-1807" data-mp="1dx3hsc1vtguv">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-1808" data-mp="3fp9i55xcyrtr"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1809" data-mp="3frm0hk71347c">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1810" data-mp="1hgvk57fbcacn">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1811" data-mp="yjaw22kdhft3"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1812" data-mp="1cik6smpt7hpo"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1813" data-mp="17guwj9aev82n"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1814" data-mp="2nv3868tn7wzi">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1815" data-mp="9pokhv79kqm5">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1816" data-mp="3rvuhnr6ahb42">
          <div data-id="element-1817" data-mp="o8gq6ayfmqw">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-1818" data-mp="pt338n47zdo">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1819" data-mp="sfeeig37d8d0">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-1820" data-mp="emyg5sl8xyif">
            <Mail size={14} data-id="element-1821" data-mp="17blut5bhppn0" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1822" data-mp="1c3nehcazmin2">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-1823" data-mp="q1leh6r1a551">
            <div className="flex items-center gap-3 mb-4" data-id="element-1824" data-mp="799c4k0dr3uw">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1825" data-mp="c1kb9knaqlme">
                <Building2 size={16} className="text-[#30718D]" data-id="element-1826" data-mp="3tj3kd8n8x22t" />
              </div>
              <div data-id="element-1827" data-mp="2aps4p5ck256">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1828" data-mp="2c93q2g25p0m">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-1829" data-mp="3pg761u4qns4o">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-1830" data-mp="35ux6bsv8iv9u">
              <div className="flex items-center gap-3" data-id="element-1831" data-mp="30w3ue4vljcbs">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-1832" data-mp="30xevfwxnq5z7">
                  <Users size={16} className="text-[#007558]" data-id="element-1833" data-mp="3athzx9fzegpb" />
                </div>
                <div data-id="element-1834" data-mp="3avuquc3pw1ei">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1835" data-mp="3oszts384wk5j">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1836" data-mp="3jtwoqis5mdxk">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-1837" data-mp="3tkhtknyf8eox">
              <div className="flex items-center gap-3" data-id="element-1838" data-mp="becy7rivb9m7">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1839" data-mp="bewez4ws7ryn">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-1840" data-mp="6d72dktwv68g" />
                </div>
                <div data-id="element-1841" data-mp="308t8r7o9l9as">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1842" data-mp="305f4ftw1iq0p">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1843" data-mp="396vg04ylcy6i">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-1844" data-mp="3j03tt87b6rur">
            <div className="flex items-center gap-3 mb-4" data-id="element-1845" data-mp="22jhskfc61ixq">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1846" data-mp="22jzvtvx8wxf5">
                <Mail size={20} className="text-[#30718D]" data-id="element-1847" data-mp="1v1jowrgo122f" />
              </div>
              <div data-id="element-1848" data-mp="1q877p2ebfxjp">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1849" data-mp="1q7xihh13huf2">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1850" data-mp="1h8k90ocz15uc">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-1851" data-mp="2dzcopkvzsvvl">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-1852" data-mp="24y4q11pkdt0z">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1853" data-mp="24wtm91w4cl9v">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1854" data-mp="1zzuw0ufksygy">
                  <Users size={18} className="text-[#30718D]" data-id="element-1855" data-mp="2cf9raswfcqdm" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1856" data-mp="2h8cmf5guo7vx">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1857" data-mp="2v7ltnfn2vrnr" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1858" data-mp="2r4pbfq1g9xkc">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1859" data-mp="1atlewix81osx">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1860" data-mp="oj37bs6qywf4">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1861" data-mp="spq6wppahzb">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1862" data-mp="2aks1msj2dj8s">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1863" data-mp="1hyh1tk3a7md1">
                <Send size={16} data-id="element-1864" data-mp="1qx1bjvqv6lp4" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-1865" data-mp="32chmsyjqelgb">
          <div className="flex items-center gap-2 mb-4" data-id="element-1866" data-mp="32by2xcaitmzn">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1867" data-mp="37b195028lz97">
              Performance Snapshot
            </h2>
            <AIInsightPopover align="left" headline="Participation is strong but completion is slipping" insightBeforeMetric="80% of learners were active this month, yet only" highlightedMetric="10%" insightAfterMetric=" have finished every module." explanation="The opportunity sits with active learners who have one or two modules left — a focused reminder should close the gap." insightId={12} data-id="element-1868" data-mp="3heg1uqlff3ex" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1869" data-mp="2toc0kpvpnfr3">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1870" data-mp="2orc4n7skrtdp">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1871" data-mp="12759hea64gcx">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1872" data-mp="168qhal7ui4g9">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1873" data-mp="16a1l2l1ajc7d">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-1874" data-mp="xf3nv1r41da8">
                    of your invited learners have
                    <br data-id="element-1875" data-mp="ihl1g61wnc5y" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-1876" data-mp="niin4sm4ioc7">
                  <span className="text-xs" data-id="element-1877" data-mp="9j9dkbsfash7">▲</span>
                  <span data-id="element-1878" data-mp="eh134z5li2i8">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1879" data-mp="eivqek4ca6ph"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1880" data-mp="28usblvc6ugc3">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1881" data-mp="2n02gjleujiad">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-1882" data-mp="2myhjnp527n9c">
                  <div className="text-gray-700 leading-relaxed" data-id="element-1883" data-mp="31nx82yh4bglv">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1884" data-mp="2wr6tygoviv33">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-1885" data-mp="36ol7a7fi33r6">
                      of your learners have
                      <br data-id="element-1886" data-mp="1gbvrtac8e5qu" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-1887" data-mp="jolmw0ko0n8e">
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-1888" data-mp="jmioot9cznsl">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1889" data-mp="eoz7plla0wwy">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1890" data-mp="3rxvarolywhkp">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-1891" data-mp="1ompce6z0zs1p">
                  <span className="text-xs" data-id="element-1892" data-mp="1eq46ljf6mcwt">▲</span>
                  <span data-id="element-1893" data-mp="1erf4x3puysgj">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1894" data-mp="2ccteg9wkfjql"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1895" data-mp="1xoq3em3o1hux">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1896" data-mp="34zt41ckaz7h3">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1897" data-mp="34x6vb9loelxa">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1898" data-mp="300znfb9pz0cp">
                    10%
                  </span>
                  <span className="align-middle text-sm" data-id="element-1899" data-mp="2qzs17rkh9pyz">
                    of your learners have
                    <br data-id="element-1900" data-mp="2m0ow674hzjr0" />
                    completed all skill modules
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-1901" data-mp="2h9gll7rflsc9">
                  <span className="text-xs" data-id="element-1902" data-mp="2cadtenwjvglp">▼</span>
                  <span data-id="element-1903" data-mp="2cb5bqx14zhu1">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-1904" data-mp="28av8hs4qb5iv"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-1905" data-mp="1ybe55hcy93q2">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} data-id="element-1906" data-mp="ir3lpw9d7eww" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1907" data-mp="itzktnwy89mx">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1908" data-mp="8tyzjwfirjg3">
                    <Icon size={24} className="text-white" data-id="element-1909" data-mp="3ciu0xf0lm2vh" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1910" data-mp="2tegps1684onn">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1911" data-mp="2oh72os6b4p2z">
                    {card.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1912" data-mp="3363cato0ia5p">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1913" data-mp="2ybyyprk8r3vn">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1914" data-mp="375vkh59hueaj" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-1915" data-mp="1rpdbjq2cyajh">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-1916" data-mp="3on13flvagzvl">
            <div className="flex items-center gap-4" data-id="element-1917" data-mp="3tksvqgzuio0b">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1918" data-mp="3fmcper5wazip">
                <HelpCircle size={24} className="text-white" data-id="element-1919" data-mp="3jp94i7w88dhw" />
              </div>
              <div data-id="element-1920" data-mp="35pq6i7x4qijb">
                <h3 className="text-xl font-bold mb-1" data-id="element-1921" data-mp="35q9ccq9i9ggz">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-1922" data-mp="3amzt7ft4wg3g">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-1923" data-mp="18ubwzqewqsx6">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1924" data-mp="17cwg9qkqx0sz">
                <Mail size={16} data-id="element-1925" data-mp="xiwd3u90y5mw" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}